import React from "react"
import { Toggle, RaisedButton, Paper } from "material-ui"
import autobind from "class-autobind"
import _ from "lodash"
import AppStateStore from "../../stores/AppStateStore"
import { green400 } from "material-ui/styles/colors"
import { IAccountDocument, EPlanType } from "../../models"
import * as accountActions from "../../actions/AccountActions"
import moment from "moment"
import strings from "../../localization/strings"
import * as utils from "../../helpers/utils"
import FreeAccountDataUsageBanner from "./FreeAccountDataUsageBanner"

interface IProps {
    account: IAccountDocument
    readOnly?: boolean
    updateFeatures?(feature: string, value: boolean): void
    updatePlan?(enableSchedule: boolean, enableTimeoff: boolean): void
}

interface IState {
    schedulingEnabled: boolean
    timeoffEnabled: boolean
}

export default class AccountSubscription extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            schedulingEnabled: this.props.account.AccountSettings.FeatureSettings.EnableSchedule,
            timeoffEnabled: this.props.account.AccountSettings.FeatureSettings.EnableAccrualsTimeoff,
        }
        autobind(this)
    }

    upgradePrompt() {
        if (this.props.account.BillingInfo.PlanType === EPlanType.Free && !this.props.account.BillingInfo.PaymentSetup) {
            AppStateStore.setConfirmDialogContent({
                content: strings.PaymentMethodMustBeSetup,
                confirm: () => { }
            })
            return
        }

        AppStateStore.setConfirmDialogContent({
            content: strings.UpgradeToProfessionalPrompt,
            confirm: () => { this.props.updatePlan ? this.props.updatePlan(this.state.schedulingEnabled, this.state.timeoffEnabled) : this.updatePlan(this.state.schedulingEnabled, this.state.timeoffEnabled) }
        })
    }

    updateFeatures(feature: string, value: boolean) {
        if (this.props.account.BillingInfo.PlanType !== EPlanType.Free) {
            if (feature === "schedule") {
                accountActions.updateScheduleFeature(this.props.account.Id, value)
            } else if (feature === "timeoff") {
                accountActions.updateTimeoffFeature(this.props.account.Id, value)
            }
        }
    }

    updatePlan(enableSchedule: boolean, enableTimeoff: boolean) {
        if (this.props.account.BillingInfo.PlanType === EPlanType.Free) {
            //Update the feature settings of the account.
            accountActions.updateAccountPlan(this.props.account.Id, EPlanType.Professional, enableSchedule, enableTimeoff)
        }
    }

    render() {
        const lightBlue = "#02bcf5"
        const headerBlue = "#e8f2fc"
        let monthlyRate = AppStateStore.perEmployeePerMonthRateInitValue
        if (this.state.schedulingEnabled) {
            monthlyRate += this.props.account.BillingInfo.PerEmployeePerMonthSchedulingRate
        }
        if (this.state.timeoffEnabled) {
            monthlyRate += this.props.account.BillingInfo.PerEmployeePerMonthTimeOffRate
        }
        const monthlyRateCurrencyFormat = utils.getCurrencyFormat(monthlyRate)
        const check = <i className="fa fa-check" style={{ fontSize: 14, color: green400 }} />
        const featureList = (
            <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                <div style={{ textAlign: "left" }}>
                    <h3 style={{ color: lightBlue, marginTop: 10, marginBottom: 5 }}>Features</h3>
                    <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
                        <li>
                            {check} Mobile Time Tracking
                        </li>
                        <li>
                            {check} Mobile Biometrics and Facial Recognition
                        </li>
                        <li>
                            {check} Time and Attendance Online Administration
                        </li>
                        <li>
                            {check} Hierarchy, Position, Job, and Phase Tracking
                        </li>
                        <li>
                            {check} Advanced Punch Permissions
                        </li>
                        <li>
                            {check} Payroll Preparation Workflow and Export
                        </li>
                        <li>
                            {check} Employee Self-Service
                        </li>
                        <li>
                            {check} Supervisor Permissions and Assignment
                        </li>
                        <li>
                            {check} Pay & Bill rate, Burden, and Profitability
                        </li>
                        <li>
                            {check} Job Dashboard, Budget & Variance Reporting
                        </li>
                        <li>
                            {check} Client Management
                        </li>
                        <li>
                            {check} Punch Question Workflows
                        </li>
                    </ul>
                </div>
            </div>
        )

        const schedulingLeaveTracking = (
            <ul style={{ listStyleType: "none", padding: 0, margin: 0, paddingLeft: 0, textAlign: "left" }}>
                <li style={{ paddingLeft: this.state.schedulingEnabled ? 18 : 38 }}>
                    {this.state.schedulingEnabled &&
                        check
                    }
                    <Toggle
                        toggled={this.state.schedulingEnabled}
                        onToggle={(event, value) => { this.setState({ schedulingEnabled: value }, () => this.props.updateFeatures ? this.props.updateFeatures("schedule", value) : this.updateFeatures("schedule", value)) }}
                        labelPosition="left"
                        label={"Enhanced Scheduling"}
                        style={{ maxWidth: 200, marginBottom: 0, display: "inline-block", marginLeft: this.state.schedulingEnabled ? 5 : 0 }}
                        disabled={this.props.readOnly}
                    /> {this.props.account.BillingInfo.PlanType === EPlanType.Professional && this.state.schedulingEnabled && <i>Starting {this.props.account.BillingInfo.SchedulingBillStartDate && moment(this.props.account.BillingInfo.SchedulingBillStartDate).format("MM/DD/YYYY")}</i>}
                </li>
                <li style={{ paddingLeft: this.state.timeoffEnabled ? 18 : 38 }}>
                    {this.state.timeoffEnabled &&
                        check
                    }
                    <Toggle
                        toggled={this.state.timeoffEnabled}
                        onToggle={(event, value) => { this.setState({ timeoffEnabled: value }, () => this.props.updateFeatures ? this.props.updateFeatures("timeoff", value) : this.updateFeatures("timeoff", value)) }}
                        labelPosition="left"
                        label={"Leave Tracking"}
                        style={{ maxWidth: 200, marginBottom: 0, display: "inline-block", marginLeft: this.state.timeoffEnabled ? 5 : 0 }}
                        disabled={this.props.readOnly}
                    /> {this.props.account.BillingInfo.PlanType === EPlanType.Professional && this.state.timeoffEnabled && <i>Starting {this.props.account.BillingInfo.TimeoffBillStartDate && moment(this.props.account.BillingInfo.TimeoffBillStartDate).format("MM/DD/YYYY")}</i>}
                </li>
            </ul>
        )

        const freeNotIncludedFeatures = (
            <ul style={{ listStyleType: "none", paddingLeft: 38, paddingRight: 20, paddingTop: 0, margin: 0, textAlign: "left" }}>
                <li>
                    Enhanced Scheduling
                </li>
                <li>
                    Leave Tracking
                </li>
                <li>
                    Custom Data Import
                </li>
                <li>
                    Custom Payroll Export
                </li>
                <li>
                    Implementation and Change Management Project
                </li>
            </ul>
        )

        const professionalNotIncludedFeatures = (
            <ul style={{ listStyleType: "none", paddingLeft: 38, paddingRight: 20, paddingTop: 0, margin: 0, textAlign: "left" }}>
                <li>
                    Custom Data Import
                </li>
                <li>
                    Custom Payroll Export
                </li>
                <li>
                    Implementation and Change Management Project
                </li>
            </ul>
        )

        const enterpriseFeatures = (
            <ul style={{ listStyleType: "none", paddingLeft: 20, paddingRight: 20, paddingTop: 0, margin: 0, textAlign: "left" }}>
                <li>
                    {check} Custom Data Import
                </li>
                <li>
                    {check} Custom Payroll Export
                </li>
                <li>
                    {check} Implementation and Change Management Project
                </li>
            </ul>
        )

        const freeDataUsage = (
            <div style={{ textAlign: "left", paddingLeft: 20, paddingRight: 20 }}>
                <h3 style={{ color: lightBlue, marginTop: -10, marginBottom: 5 }}>Data Usage</h3>
                <FreeAccountDataUsageBanner
                    showInSubscriptionDrawer
                    accountId={this.props.account.Id}
                />
            </div>
        )

        const paidDataUsage = (
            <div style={{ textAlign: "left", paddingLeft: 20, paddingRight: 20, height: 150 }}>
                <h3 style={{ color: lightBlue, marginTop: -10, marginBottom: 5 }}>Data Usage</h3>
                <div>Unlimited Punches</div>
                <div>Unlimited Employees</div>
                <div>Unlimited Jobs</div>
                <div>Unlimited Clients</div>
            </div>
        )

        const upgrade = (
            <div style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: 20, paddingRight: 20, margin: 0, marginTop: 10 }}>
                <RaisedButton
                    data-intercom-target={"Paywall-Drawer-Btn-Upgrade"}
                    backgroundColor="white"
                    labelColor="#1181F2"
                    label={strings.Upgrade}
                    style={{ width: "100%", marginTop: 0, border: "2px solid #1181F2" }}
                    onTouchTap={() => { this.upgradePrompt() }}
                    disabled={this.props.readOnly}
                />
            </div>
        )

        const talkToSales = (
            <div style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 0, margin: 0, marginTop: 10 }}>
                <RaisedButton
                    data-intercom-target={"Paywall-Drawer-Btn-Talk-to-sales"}
                    backgroundColor="#1181F2"
                    labelColor="white"
                    label={strings.TalkToSales}
                    style={{ width: "100%" }}
                    onTouchTap={() => { window["Intercom"]("show") }}
                    disabled={this.props.readOnly}
                />
            </div>
        )

        const baseRate = (
            <div>
                + ${this.props.account.BillingInfo.MonthlyBaseRate} Monthly Base Rate
            </div>
        )
        const sectionHeaderStyle = {
            padding: 10, height: 120
        }

        return (
            <div style={{ display: "flex", flexDirection: "row", textAlign: "center" }}>
                <Paper style={{ width: "33%", backgroundColor: this.props.account.BillingInfo.PlanType === EPlanType.Free ? headerBlue : null }}>
                    <div style={sectionHeaderStyle}>
                        <h3 style={{ color: this.props.account.BillingInfo.PlanType === EPlanType.Free ? lightBlue : null }}>Free Plan</h3>
                        <div style={{ fontSize: 25 }}>$0/mo</div>
                        <div>{strings.PerEmployee}</div>
                    </div>
                    {freeDataUsage}
                    {featureList}
                    {freeNotIncludedFeatures}
                    {this.props.account.BillingInfo.PlanType === EPlanType.Free &&
                        <div style={{ backgroundColor: lightBlue, color: "white", padding: 10, marginTop: 105 }}>
                            <i className="fa fa-star" /> {strings.SelectedPlan}
                        </div>
                    }
                </Paper>
                <Paper style={{ width: "33%", backgroundColor: this.props.account.BillingInfo.PlanType === EPlanType.Professional ? headerBlue : null }}>
                    <div style={sectionHeaderStyle}>
                        <h3 style={{ color: this.props.account.BillingInfo.PlanType === EPlanType.Professional ? lightBlue : null }}>Professional Plan</h3>
                        <div style={{ fontSize: 25 }}>{monthlyRateCurrencyFormat}/mo</div>
                        <div>{strings.PerEmployee}</div>
                        {baseRate}
                        {this.props.account.BillingInfo.PlanType === EPlanType.Professional && this.props.account.BillingInfo.PlanStartDate && <i>Starting {moment(this.props.account.BillingInfo.PlanStartDate).format("MM/DD/YYYY")}</i>}
                    </div>
                    {paidDataUsage}
                    {featureList}
                    {schedulingLeaveTracking}
                    {professionalNotIncludedFeatures}
                    {this.props.account.BillingInfo.PlanType === EPlanType.Free &&
                        <div style={{ marginTop: 40 }}>
                            {talkToSales}
                        </div>
                    }
                    {this.props.account.BillingInfo.PlanType === EPlanType.Free &&
                        <div>{upgrade}</div>
                    }
                    {this.props.account.BillingInfo.PlanType === EPlanType.Professional &&
                        <div style={{ backgroundColor: lightBlue, color: "white", padding: 10, marginTop: 105 }}>
                            <i className="fa fa-star" /> {strings.SelectedPlan}
                        </div>
                    }
                </Paper>
                <Paper style={{ width: "33%", backgroundColor: this.props.account.BillingInfo.PlanType === EPlanType.Enterprise ? headerBlue : null }}>
                    <div style={sectionHeaderStyle}>
                        <h3 style={{ color: this.props.account.BillingInfo.PlanType === EPlanType.Enterprise ? lightBlue : null }}>Enterprise Plan</h3>
                        <div>Talk to sales for pricing information</div>
                        {this.props.account.BillingInfo.PlanType === EPlanType.Enterprise && this.props.account.BillingInfo.PlanStartDate && <i>Starting {moment(this.props.account.BillingInfo.PlanStartDate).format("MM/DD/YYYY")}</i>}
                    </div>
                    {paidDataUsage}
                    {featureList}
                    {schedulingLeaveTracking}
                    {enterpriseFeatures}
                    {this.props.account.BillingInfo.PlanType !== EPlanType.Enterprise &&
                        <div style={{ marginTop: 40 }}>
                            {talkToSales}
                        </div>
                    }
                    {this.props.account.BillingInfo.PlanType === EPlanType.Enterprise &&
                        <div style={{ backgroundColor: lightBlue, color: "white", padding: 10, marginTop: 105 }}>
                            <i className="fa fa-star" /> {strings.SelectedPlan}
                        </div>
                    }
                </Paper>
            </div>
        )
    }
}